import { Injectable } from "@angular/core";
import { ExportContext, ExportFileFormat, ExportRange, ExportService } from "app/layout/export-overlay";
import { get } from "lodash-es";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { takeUntil } from "rxjs";
import ExpenseAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/expense/expense.entity";
import { ExpenseService } from "app/modules/components/payment-master/expense/expense-list/expense-list.service";
import { EXPENSE_PROPERTIES, CURRENCYFIELDS, FORMATTER, DATE_FIELDS, DATE_TIME_FORMATTER } from "./expense-report.constants";

export interface ExpenseExportData {
    trip: number,
    subRoute: string,
    serial: string,
    username: string,
    responsibleName: string,
    reasonForExpense: string,
    typeDocument: string,
    classDocument: string,
    amount: number,
    creationDateTime: string,
    updatedDateTime: string,
    comment: string
}

@Injectable()
export class ExpenseReportExport {
    constructor(
        private exportService: ExportService,
        private expenseService: ExpenseService,
    ) { }


    private transformData(lossList: ExpenseAttributes[]): ExpenseExportData[] {
        return lossList?.flatMap((item: ExpenseAttributes) => ({
            trip: get(item, "trip.id", 0),
            subRoute: get(item, "subRoute.name", ""),
            serial: get(item, "serial", ""),
            username: get(item, "user.user_login.username", ""),
            responsibleName: get(item, "responsibleBusinessPartner.name", ""),
            reasonForExpense: get(item, "reasonForExpense.name", ""),
            typeDocument: get(item, "type.name", ""),
            classDocument: get(item, "class.name", ""),
            amount: get(item, "amount", 0),
            creationDateTime: item.creationDateTime as unknown as string,
            updatedDateTime: item.updatedDateTime as unknown as string,
            comment: get(item, "observation", "")
        }));
    }

    export(
        format: ExportFileFormat,
        range: ExportRange,
        totalItems: number,
        pageSize: number,
        pageIndex: number,
        exportId: string,
        exportTitle: string,
        properties: typeof EXPENSE_PROPERTIES,
        filters: IFilters,
        optionsFilters: IPagination<ExpenseAttributes>
    ) {
        this.exportService
            .task({
                id: exportId,
                title: exportTitle,
                format,
                iterations: 1,
                start: (ctx: ExportContext) => {
                    ctx.processor.localizeItemProperties(
                        exportTitle,
                        properties ?? EXPENSE_PROPERTIES
                    );
                },
                next: (ctx: ExportContext) => {
                    optionsFilters.limitPerPage = range === ExportRange.AllPages ? totalItems : pageSize;
                    optionsFilters.currentPage = range === ExportRange.AllPages ? 1 : pageIndex;

                    this.expenseService
                        .findAllWithCount(
                            optionsFilters,
                            filters
                        )
                        .pipe(takeUntil(ctx.stopped))
                        .subscribe({
                            next: (res: IPaginationResult<ExpenseAttributes>) => {
                                const formattedData = res?.data[0].data.flatMap((item: ExpenseAttributes) => {

                                    CURRENCYFIELDS.forEach((field) => {
                                        const value = item[field];
                                        if (value !== undefined && value !== null) {
                                            const numericValue = Number(value);
                                            if (!isNaN(numericValue)) {
                                                item[field] = FORMATTER.format(numericValue);
                                            }
                                        }
                                    });

                                    DATE_FIELDS.forEach((field) => {
                                        const dateValue = item[field as keyof ExpenseAttributes];
                                        if (dateValue instanceof Date) {
                                            (item as any)[field] = DATE_TIME_FORMATTER.format(dateValue);
                                        }
                                    });


                                    const itemForExport = {
                                        ...item,
                                    };

                                    return itemForExport;
                                });

                                const lossList = formattedData;
                                const rows = this.transformData(lossList);

                                ctx.processor.addToSection(exportTitle, rows);
                                ctx.next();
                            },
                        });
                },
            })
            .start();
    }

    canStartTask(exportId: string): boolean {
        if (this.exportService.taskIsActive(exportId)) {
            this.exportService.setVisible(true);
            return false;
        }
        return true;
    }
}